'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BusinessInfoFormProps {
  onDataChange: (data: BusinessInfoData) => void;
  initialData?: BusinessInfoData;
}

export interface BusinessInfoData {
  shopName: string;
  ownerName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  website: string;
  yearsInBusiness: string;
}

const BusinessInfoForm = ({ onDataChange, initialData }: BusinessInfoFormProps) => {
  const [formData, setFormData] = useState<BusinessInfoData>(
    initialData || {
      shopName: '',
      ownerName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: 'CA',
      zipCode: '',
      website: '',
      yearsInBusiness: ''
    }
  );

  const [errors, setErrors] = useState<Partial<Record<keyof BusinessInfoData, string>>>({});

  const handleChange = (field: keyof BusinessInfoData, value: string) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onDataChange(updatedData);

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    return /^\(\d{3}\)\s\d{3}-\d{4}$/.test(phone);
  };

  const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhone(value);
    handleChange('phone', formatted);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start space-x-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
          <Icon name="BuildingStorefrontIcon" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
            Business Information
          </h3>
          <p className="font-body text-sm text-text-secondary">
            Provide accurate business details for verification
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="shopName" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Shop Name <span className="text-error">*</span>
          </label>
          <input
            id="shopName"
            type="text"
            value={formData.shopName}
            onChange={(e) => handleChange('shopName', e.target.value)}
            placeholder="Enter your shop name"
            className={`w-full rounded-lg border ${
              errors.shopName ? 'border-error' : 'border-border'
            } bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
            required
          />
          {errors.shopName && (
            <p className="font-caption text-xs text-error flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.shopName}</span>
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="ownerName" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Owner Name <span className="text-error">*</span>
          </label>
          <input
            id="ownerName"
            type="text"
            value={formData.ownerName}
            onChange={(e) => handleChange('ownerName', e.target.value)}
            placeholder="Enter owner's full name"
            className={`w-full rounded-lg border ${
              errors.ownerName ? 'border-error' : 'border-border'
            } bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
            required
          />
          {errors.ownerName && (
            <p className="font-caption text-xs text-error flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.ownerName}</span>
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Business Email <span className="text-error">*</span>
          </label>
          <div className="relative">
            <Icon
              name="EnvelopeIcon"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="shop@example.com"
              className={`w-full rounded-lg border ${
                errors.email ? 'border-error' : 'border-border'
              } bg-input pl-10 pr-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
              required
            />
          </div>
          {errors.email && (
            <p className="font-caption text-xs text-error flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.email}</span>
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="phone" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Business Phone <span className="text-error">*</span>
          </label>
          <div className="relative">
            <Icon
              name="PhoneIcon"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="(555) 123-4567"
              className={`w-full rounded-lg border ${
                errors.phone ? 'border-error' : 'border-border'
              } bg-input pl-10 pr-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
              required
            />
          </div>
          {errors.phone && (
            <p className="font-caption text-xs text-error flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.phone}</span>
            </p>
          )}
        </div>

        <div className="space-y-2 lg:col-span-2">
          <label htmlFor="address" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Street Address <span className="text-error">*</span>
          </label>
          <input
            id="address"
            type="text"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder="123 Main Street"
            className={`w-full rounded-lg border ${
              errors.address ? 'border-error' : 'border-border'
            } bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
            required
          />
          {errors.address && (
            <p className="font-caption text-xs text-error flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.address}</span>
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="city" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            City <span className="text-error">*</span>
          </label>
          <input
            id="city"
            type="text"
            value={formData.city}
            onChange={(e) => handleChange('city', e.target.value)}
            placeholder="San Francisco"
            className={`w-full rounded-lg border ${
              errors.city ? 'border-error' : 'border-border'
            } bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
            required
          />
          {errors.city && (
            <p className="font-caption text-xs text-error flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.city}</span>
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="state" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
              State <span className="text-error">*</span>
            </label>
            <select
              id="state"
              value={formData.state}
              onChange={(e) => handleChange('state', e.target.value)}
              className="w-full rounded-lg border border-border bg-input px-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              required
            >
              <option value="CA">California</option>
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="zipCode" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
              ZIP Code <span className="text-error">*</span>
            </label>
            <input
              id="zipCode"
              type="text"
              value={formData.zipCode}
              onChange={(e) => handleChange('zipCode', e.target.value)}
              placeholder="94102"
              maxLength={5}
              className={`w-full rounded-lg border ${
                errors.zipCode ? 'border-error' : 'border-border'
              } bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
              required
            />
            {errors.zipCode && (
              <p className="font-caption text-xs text-error flex items-center space-x-1">
                <Icon name="ExclamationCircleIcon" size={14} />
                <span>{errors.zipCode}</span>
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="website" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Website (Optional)
          </label>
          <div className="relative">
            <Icon
              name="GlobeAltIcon"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://yourshop.com"
              className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="yearsInBusiness" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Years in Business <span className="text-error">*</span>
          </label>
          <select
            id="yearsInBusiness"
            value={formData.yearsInBusiness}
            onChange={(e) => handleChange('yearsInBusiness', e.target.value)}
            className={`w-full rounded-lg border ${
              errors.yearsInBusiness ? 'border-error' : 'border-border'
            } bg-input px-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
            required
          >
            <option value="">Select years</option>
            <option value="0-2">0-2 years</option>
            <option value="3-5">3-5 years</option>
            <option value="6-10">6-10 years</option>
            <option value="11-20">11-20 years</option>
            <option value="20+">20+ years</option>
          </select>
          {errors.yearsInBusiness && (
            <p className="font-caption text-xs text-error flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.yearsInBusiness}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessInfoForm;